import { useEffect, useRef } from "react";
import * as ScreenOrientation from "expo-screen-orientation";

export interface UseLandscapeLockOptions {
  /**
   * Whether to lock orientation to landscape.
   * When true, locks to LANDSCAPE. When false or unmount, resets to PORTRAIT_UP.
   */
  enabled: boolean;
  /**
   * Delay in milliseconds before locking orientation.
   * Useful for iOS stability (default 500ms).
   * Use 0 for immediate lock.
   * @default 500
   */
  delay?: number;
}

/**
 * Hook to manage landscape orientation lock for camera streaming screens.
 *
 * Locks device orientation to landscape when enabled, with optional delay.
 * Automatically resets to portrait on unmount or when disabled.
 * Handles cleanup to prevent orientation changes after component unmount.
 *
 * @param options - Configuration options
 * @returns void
 *
 * @example
 * // Lock immediately
 * useLandscapeLock({ enabled: true, delay: 0 });
 *
 * @example
 * // Lock with 500ms delay (iOS stability)
 * useLandscapeLock({ enabled: true });
 *
 * @example
 * // Conditional lock based on state
 * useLandscapeLock({ enabled: hasLivestream, delay: 500 });
 */
export function useLandscapeLock(options: UseLandscapeLockOptions): void {
  const { enabled, delay = 500 } = options;
  const isMountedRef = useRef(true);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let orientationLockTimeout: ReturnType<typeof setTimeout> | null = null;
    let isEffectCancelled = false;

    const lockOrientation = async () => {
      if (isEffectCancelled || !isMountedRef.current) {
        return;
      }
      try {
        await ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.LANDSCAPE);
      } catch {
        // Orientation lock not supported on this device
      }
    };

    orientationLockTimeout = setTimeout(() => {
      orientationLockTimeout = null;
      lockOrientation();
    }, delay);

    return () => {
      isEffectCancelled = true;
      if (orientationLockTimeout) {
        clearTimeout(orientationLockTimeout);
      }
      // Reset to portrait on unmount
      setTimeout(() => {
        ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP).catch(() => {
          // Orientation lock not supported
        });
      }, 0);
    };
  }, [enabled, delay]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
}
